import DialogTitle from '@apps-common/ui-components/DialogTitle.jsx';
import Dialog from '@mui/material/Dialog';
import {EVENT_ACTIONS} from '@src/analytics.js';
import {useTrackSectionEvent} from '@src/hooks/useTrackSectionEvent.js';
import {useStore} from '@src/stores/useStore.ts';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import UploadStep from './UploadStep.jsx';
import {getExtension, groupFiles} from './util.js';
import ValidationStep from './ValidationStep.jsx';

const UploadDialog = ({inFiles = [], onClose}) => {
  const trackSectionEvent = useTrackSectionEvent();
  const setFeature = useStore(state => state.map.setMapFeature);

  const [fileGroups, setFileGroups] = useState([]);
  const [activeGroupIndex, setActiveGroupIndex] = useState(-1);

  const groupedFilesKeys = useMemo(() => Object.keys(fileGroups), [fileGroups]);
  const files = useMemo(
    () => groupedFilesKeys.flatMap(key => fileGroups[key].files),
    [fileGroups, groupedFilesKeys],
  );
  const activeGroup = fileGroups[groupedFilesKeys[activeGroupIndex]];

  const onDropAccepted = useCallback(acceptedFiles => {
    const groups = groupFiles({
      incoming: acceptedFiles,
      existing: fileGroups,
    });
    setFileGroups(groups);
    setActiveGroupIndex(0);

    acceptedFiles.forEach(file => {
      trackSectionEvent(EVENT_ACTIONS.DROP, 'accepted file', {
        extension: getExtension(file.name),
        bytes: file.size,
      });
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDropRejected = rejectedFiles => {
    //track rejected file extension and file size
    rejectedFiles.forEach(file => {
      trackSectionEvent(EVENT_ACTIONS.DROP, 'rejected file', {
        extension: getExtension(file.name),
        bytes: file.size,
      });
    });
  };

  const onImport = feature => {
    setFeature(feature);
    onClose();
  };

  useEffect(() => {
    if (inFiles?.length > 0) {
      onDropAccepted(inFiles);
    }
  }, [inFiles, onDropAccepted]);

  const title = activeGroup
    ? `Import ${activeGroup.label} ${
        groupedFilesKeys.length > 1
          ? `(${activeGroupIndex + 1}/${groupedFilesKeys.length})`
          : ''
      }`
    : 'Import files';

  return (
    <Dialog
      open
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown
      slotProps={{
        paper: {
          sx: {height: 700},
        },
      }}
      aria-labelledby="upload-dialog-title"
    >
      <DialogTitle id="upload-dialog-title" title={title} onClose={onClose} />

      {!activeGroup && (
        <UploadStep
          onDropAccepted={onDropAccepted}
          onDropRejected={onDropRejected}
          files={files}
          sx={{
            overflow: 'hidden',
          }}
          onClose={onClose}
        />
      )}

      {!!activeGroup && (
        <ValidationStep
          group={activeGroup}
          onImport={onImport}
          onClose={onClose}
        />
      )}
    </Dialog>
  );
};

UploadDialog.propTypes = {
  inFiles: PropTypes.array,
  onClose: PropTypes.func,
};

export default UploadDialog;
